import { type NoiseColor, noiseBuffer, Synth } from './synth';
import type { AmbienceId } from './themes';

/**
 * Environmental beds on the ambient bus: looped filtered noise shaped by slow
 * LFOs (wind, surf, cave rumble) plus sparse synthesised events (birdsong,
 * gulls, water drips, ice crackle, distant bells). Beds crossfade like music.
 */

type EventKind = 'bird' | 'gull' | 'drip' | 'whistle' | 'crackle' | 'groan' | 'wave' | 'chime';

interface BedLayer {
  color: NoiseColor;
  type: BiquadFilterType;
  freq: number;
  q: number;
  gain: number;
  /** [rate Hz, depth as a fraction of freq] */
  freqLfo?: [number, number];
  /** [rate Hz, depth 0..1] */
  ampLfo?: [number, number];
}

interface BedDef {
  layers: BedLayer[];
  events: { kind: EventKind; every: [number, number]; gain: number }[];
  /** Reverb send. */
  wet: number;
}

export const BEDS: Record<Exclude<AmbienceId, 'none'>, BedDef> = {
  meadow: {
    layers: [
      { color: 'pink', type: 'lowpass', freq: 650, q: 0.5, gain: 0.07, ampLfo: [0.07, 0.5] },
      { color: 'white', type: 'highpass', freq: 5500, q: 0.5, gain: 0.012, ampLfo: [0.11, 0.6] },
    ],
    events: [{ kind: 'bird', every: [2.5, 7], gain: 1 }],
    wet: 0.25,
  },
  town: {
    layers: [
      { color: 'brown', type: 'bandpass', freq: 380, q: 0.6, gain: 0.06, ampLfo: [0.18, 0.3] },
      { color: 'pink', type: 'lowpass', freq: 900, q: 0.5, gain: 0.02 },
    ],
    events: [
      { kind: 'bird', every: [6, 14], gain: 0.8 },
      { kind: 'chime', every: [25, 50], gain: 1 },
    ],
    wet: 0.3,
  },
  desert: {
    layers: [
      {
        color: 'pink',
        type: 'bandpass',
        freq: 900,
        q: 0.9,
        gain: 0.06,
        freqLfo: [0.06, 0.45],
        ampLfo: [0.1, 0.55],
      },
      { color: 'white', type: 'highpass', freq: 4000, q: 0.5, gain: 0.01, ampLfo: [0.13, 0.7] },
    ],
    events: [{ kind: 'whistle', every: [9, 18], gain: 1 }],
    wet: 0.2,
  },
  frost: {
    layers: [
      {
        color: 'pink',
        type: 'bandpass',
        freq: 620,
        q: 3.5,
        gain: 0.06,
        freqLfo: [0.08, 0.35],
        ampLfo: [0.09, 0.5],
      },
      { color: 'brown', type: 'lowpass', freq: 260, q: 0.5, gain: 0.06 },
    ],
    events: [
      { kind: 'whistle', every: [7, 15], gain: 1 },
      { kind: 'crackle', every: [10, 22], gain: 0.8 },
    ],
    wet: 0.35,
  },
  dungeon: {
    layers: [
      { color: 'brown', type: 'lowpass', freq: 140, q: 0.7, gain: 0.09, ampLfo: [0.05, 0.3] },
    ],
    events: [
      { kind: 'drip', every: [1.5, 5], gain: 1 },
      { kind: 'groan', every: [18, 35], gain: 1 },
    ],
    wet: 0.7,
  },
  rift: {
    layers: [
      { color: 'brown', type: 'lowpass', freq: 120, q: 0.7, gain: 0.08, ampLfo: [0.21, 0.4] },
      { color: 'pink', type: 'bandpass', freq: 300, q: 6, gain: 0.02, freqLfo: [0.05, 0.5] },
    ],
    events: [
      { kind: 'crackle', every: [3, 8], gain: 1 },
      { kind: 'groan', every: [9, 18], gain: 1 },
    ],
    wet: 0.5,
  },
  sea: {
    layers: [
      { color: 'brown', type: 'lowpass', freq: 450, q: 0.5, gain: 0.1, ampLfo: [0.11, 0.7] },
      { color: 'pink', type: 'highpass', freq: 2200, q: 0.5, gain: 0.018, ampLfo: [0.11, 0.7] },
    ],
    events: [
      { kind: 'gull', every: [6, 15], gain: 1 },
      { kind: 'wave', every: [4, 9], gain: 1 },
    ],
    wet: 0.3,
  },
};

/** Synthesises one ambient event into `s` (offsets relative to its start). */
export function ambientEvent(kind: EventKind, s: Synth, level = 1): void {
  switch (kind) {
    case 'bird': {
      const calls = 2 + Math.floor(s.rand() * 4);
      const base = s.r(2600, 4200);
      let t = 0;
      for (let i = 0; i < calls; i++) {
        const f = base * s.r(0.9, 1.15);
        s.tone({
          freq: f,
          path: [
            [0.025, f * s.r(1.15, 1.35)],
            [0.07, f * s.r(0.7, 0.9)],
          ],
          at: t,
          attack: 0.006,
          decay: 0.07,
          peak: 0.1 * level,
        });
        t += s.r(0.08, 0.16);
      }
      return;
    }
    case 'gull': {
      const calls = 1 + Math.floor(s.rand() * 3);
      let t = 0;
      for (let i = 0; i < calls; i++) {
        const f = s.r(1100, 1450);
        s.tone({
          type: 'triangle',
          freq: f,
          path: [
            [0.08, f * 1.25],
            [0.35, f * 0.7],
          ],
          at: t,
          attack: 0.03,
          hold: 0.12,
          decay: 0.2,
          peak: 0.07 * level,
          filter: { type: 'bandpass', freq: 1600, q: 1.2 },
        });
        s.burst({
          color: 'pink',
          at: t,
          attack: 0.03,
          hold: 0.1,
          decay: 0.15,
          peak: 0.012 * level,
          filter: { type: 'bandpass', freq: 2500, q: 2 },
        });
        t += s.r(0.4, 0.55);
      }
      return;
    }
    case 'drip': {
      const f = s.r(700, 1100);
      s.tone({ freq: f, to: f * 2.2, sweep: 0.02, attack: 0.001, decay: 0.07, peak: 0.13 * level });
      if (s.rand() < 0.4) {
        const f2 = f * s.r(1.1, 1.4);
        s.tone({
          freq: f2,
          to: f2 * 2,
          sweep: 0.02,
          at: s.r(0.1, 0.22),
          attack: 0.001,
          decay: 0.05,
          peak: 0.07 * level,
        });
      }
      return;
    }
    case 'whistle': {
      const f = s.r(600, 1000);
      s.burst({
        color: 'pink',
        attack: 0.9,
        hold: 0.4,
        decay: 1.4,
        peak: 0.16 * level,
        filter: {
          type: 'bandpass',
          freq: f,
          q: 14,
          path: [
            [1.2, f * 1.4],
            [2.6, f * 0.8],
          ],
        },
      });
      return;
    }
    case 'crackle':
      s.grains({
        count: 5 + Math.floor(s.rand() * 5),
        spread: 0.4,
        freq: [2500, 7000],
        decay: [0.002, 0.008],
        peak: 0.1 * level,
      });
      return;
    case 'groan': {
      const f = s.r(48, 62);
      for (const det of [-10, 12]) {
        s.tone({
          type: 'sawtooth',
          freq: f,
          detune: det,
          path: [[1.5, f * 0.85]],
          attack: 1,
          hold: 0.5,
          decay: 1.5,
          peak: 0.05 * level,
          filter: { type: 'lowpass', freq: 260, q: 2 },
        });
      }
      return;
    }
    case 'wave':
      s.burst({
        color: 'brown',
        attack: 1.1,
        hold: 0.3,
        decay: 1.8,
        peak: 0.28 * level,
        filter: {
          type: 'lowpass',
          freq: 300,
          path: [
            [1.2, 1400],
            [3, 350],
          ],
        },
      });
      s.burst({
        color: 'pink',
        at: 0.4,
        attack: 1,
        decay: 1.2,
        peak: 0.04 * level,
        filter: { type: 'highpass', freq: 2500 },
      });
      return;
    case 'chime': {
      const f = s.pick([523.25, 587.33, 659.25, 783.99]);
      s.partials({
        freq: f,
        ratios: [1, 2.76, 5.4],
        amps: [1, 0.4, 0.15],
        decays: [2.5, 1.2, 0.6],
        decay: 2.5,
        peak: 0.04 * level,
      });
      return;
    }
  }
}

class AmbienceBed {
  readonly out: GainNode;
  private readonly sources: AudioScheduledSourceNode[] = [];
  private readonly nodes: AudioNode[] = [];
  private readonly nextEvent: number[];
  stopAt: number | null = null;

  constructor(
    private readonly ctx: BaseAudioContext,
    readonly def: BedDef,
    dest: { dry: AudioNode; wet: AudioNode },
    now: number,
  ) {
    this.out = ctx.createGain();
    this.out.gain.value = 0;
    this.out.connect(dest.dry);
    const wet = ctx.createGain();
    wet.gain.value = def.wet;
    this.out.connect(wet);
    wet.connect(dest.wet);
    this.nodes.push(this.out, wet);
    for (const l of def.layers) this.layer(l, now);
    this.nextEvent = def.events.map((e) => now + e.every[0] * (0.3 + Math.random() * 0.7));
  }

  private layer(l: BedLayer, now: number): void {
    const ctx = this.ctx;
    const src = ctx.createBufferSource();
    src.buffer = noiseBuffer(ctx, l.color);
    src.loop = true;
    const f = ctx.createBiquadFilter();
    f.type = l.type;
    f.frequency.value = l.freq;
    f.Q.value = l.q;
    const g = ctx.createGain();
    g.gain.value = l.ampLfo ? l.gain * (1 - l.ampLfo[1] / 2) : l.gain;
    src.connect(f);
    f.connect(g);
    g.connect(this.out);
    src.start(now, Math.random() * 3);
    this.sources.push(src);
    this.nodes.push(src, f, g);
    if (l.freqLfo) this.lfo(l.freqLfo[0], l.freq * l.freqLfo[1], f.frequency, now);
    if (l.ampLfo) this.lfo(l.ampLfo[0], (l.gain * l.ampLfo[1]) / 2, g.gain, now);
  }

  private lfo(rate: number, depth: number, param: AudioParam, now: number): void {
    const o = this.ctx.createOscillator();
    o.frequency.value = rate * (0.85 + Math.random() * 0.3);
    const g = this.ctx.createGain();
    g.gain.value = depth;
    o.connect(g);
    g.connect(param);
    o.start(now);
    this.sources.push(o);
    this.nodes.push(o, g);
  }

  fade(now: number, to: number, dur: number): void {
    fadeParam(this.out.gain, now, to, dur);
    if (to <= 0) this.stopAt = now + dur;
  }

  pump(now: number, horizon: number): void {
    if (this.stopAt !== null) return;
    this.def.events.forEach((e, i) => {
      let t = this.nextEvent[i] ?? now;
      if (t < now - 1) t = now + Math.random();
      while (t < horizon) {
        this.spawn(e.kind, Math.max(t, now), e.gain);
        t += e.every[0] + Math.random() * (e.every[1] - e.every[0]);
      }
      this.nextEvent[i] = t;
    });
  }

  private spawn(kind: EventKind, t: number, level: number): void {
    const ctx = this.ctx;
    let dest: AudioNode = this.out;
    if (typeof ctx.createStereoPanner === 'function') {
      const p = ctx.createStereoPanner();
      p.pan.value = Math.random() * 1.6 - 0.8;
      p.connect(this.out);
      dest = p;
    }
    ambientEvent(kind, new Synth(ctx, dest, t, { pitch: 0.95 + Math.random() * 0.1 }), level);
  }

  finished(now: number): boolean {
    return this.stopAt !== null && now > this.stopAt + 0.5;
  }

  dispose(): void {
    const now = this.ctx.currentTime;
    for (const s of this.sources) {
      try {
        s.stop(now);
      } catch {
        // already stopped
      }
    }
    for (const n of this.nodes) n.disconnect();
  }
}

/** Equal-power style fade from the current value (safe to interrupt). */
export function fadeParam(param: AudioParam, now: number, to: number, dur: number): void {
  const from = param.value;
  param.cancelScheduledValues(now);
  param.setValueAtTime(from, now);
  if (dur <= 0.01) {
    param.setValueAtTime(to, now);
    return;
  }
  const rising = to > from;
  const steps = 8;
  for (let i = 1; i <= steps; i++) {
    const x = i / steps;
    // Rising: sin curve; falling: cos curve. Both preserve summed power in a crossfade.
    const w = rising ? Math.sin((x * Math.PI) / 2) : 1 - Math.cos((x * Math.PI) / 2);
    param.linearRampToValueAtTime(from + (to - from) * w, now + dur * x);
  }
}

/** Crossfading owner of the current ambience bed. */
export class AmbienceMixer {
  private beds: AmbienceBed[] = [];
  private current: AmbienceBed | null = null;
  private currentId: AmbienceId = 'none';

  constructor(
    private readonly ctx: BaseAudioContext,
    private readonly dest: { dry: AudioNode; wet: AudioNode },
  ) {}

  get id(): AmbienceId {
    return this.currentId;
  }

  set(id: AmbienceId, now: number, fade = 4): void {
    if (id === this.currentId) return;
    this.currentId = id;
    this.current?.fade(now, 0, fade);
    this.current = null;
    if (id === 'none') return;
    const bed = new AmbienceBed(this.ctx, BEDS[id], this.dest, now);
    bed.fade(now, 1, fade);
    this.beds.push(bed);
    this.current = bed;
  }

  pump(now: number, horizon: number): void {
    for (const b of this.beds) b.pump(now, horizon);
    const keep: AmbienceBed[] = [];
    for (const b of this.beds) {
      if (b.finished(now)) b.dispose();
      else keep.push(b);
    }
    this.beds = keep;
  }

  dispose(): void {
    for (const b of this.beds) b.dispose();
    this.beds = [];
    this.current = null;
    this.currentId = 'none';
  }
}
